import { useEffect, useMemo, useRef } from 'react';
import { useGetKitAssetVersionsQuery } from '../../../../../redux/api/fymAPI';
import { useQueryParam } from '../../../../../utils/hooks/useQueryParam';
import { SerializedReel } from '../../types';
import { parseReleaseRevision } from '../releasePageLink';
import { RevisionCard, reelVariant } from './revisionCards';

interface UseAssetDeepLinkProps {
  accountId: number;
  catalogItemUuid: string;
  cards: RevisionCard[];
  reels: SerializedReel[];
  /** Opens the viewer on a card, at a revision. */
  onOpen: (card: RevisionCard, revision?: number) => void;
  /** The asset of the link is not on this release (deleted, another song, a bad uuid). */
  onMissing: () => void;
}

/**
 * The deep link of an asset (design c2, e4): `?asset=<uuid>&rev=<n>` opens the viewer on the card of
 * that asset, at revision n. The uuid can be any revision (a Work row names the revision it made):
 * a kit asset that is not the newest is found by its versions answer, a reel by its variant. It
 * runs once per link; a link that finds no card calls `onMissing`, so the page can drop it.
 */
export function useAssetDeepLink({
  accountId,
  catalogItemUuid,
  cards,
  reels,
  onOpen,
  onMissing,
}: UseAssetDeepLinkProps): void {
  const asset = useQueryParam('asset', undefined, '');
  const revision = parseReleaseRevision(useQueryParam('rev', undefined, ''));
  const done = useRef<string | null>(null);

  const direct = useMemo(
    () =>
      asset === ''
        ? undefined
        : cards.find((card) =>
            [card.asset?.uuid, card.reel?.uuid, card.readyAsset?.uuid, card.readyReel?.uuid].includes(asset)
          ),
    [asset, cards]
  );
  const reel = useMemo(() => reels.find((one) => one.uuid === asset), [asset, reels]);
  const lookUp = asset !== '' && direct === undefined && reel === undefined;
  const versions = useGetKitAssetVersionsQuery({ accountId, catalogItemUuid, assetUuid: asset }, { skip: !lookUp });

  const target = useMemo(() => {
    if (direct !== undefined) {
      return direct;
    }
    if (reel !== undefined) {
      return cards.find((card) => card.key === `reel:${reelVariant(reel)}`);
    }
    const newest = versions.data?.versions[0];
    return newest === undefined
      ? undefined
      : cards.find((card) => card.key === `kitAsset:${newest.themeId}:${newest.variant}`);
  }, [cards, direct, reel, versions.data]);

  // The versions answer came (or failed), or no answer was needed, and still no card matches.
  const lookUpDone = !lookUp || (!versions.isUninitialized && !versions.isFetching);
  const isMissing = asset !== '' && target === undefined && lookUpDone;

  useEffect(() => {
    if (asset === '') {
      // The link is gone (the viewer closed, Back): the same link opens the viewer again.
      done.current = null;
      return;
    }
    if (done.current === asset) {
      return;
    }
    if (isMissing) {
      done.current = asset;
      onMissing();
      return;
    }
    if (target === undefined) {
      return;
    }
    done.current = asset;
    onOpen(target, revision ?? reel?.revision ?? undefined);
  }, [asset, isMissing, onMissing, onOpen, reel, revision, target]);
}
